import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { getApiBaseUrl } from "../config/api";
import { useAuth } from "../context/AuthContext";
import { parseStoneFindingsResponse } from "../stones/parseStoneFindings";
import type { StoneFinding } from "../stones/types";
import fixture from "../stones/__fixtures__/stoneFindings.sample.json";

// Explicit opt-in fixture for eval bring-up before the inference endpoint ships.
// NOT a fallback: only used when the flag is set; otherwise real errors surface.
const USE_FIXTURE =
  new URLSearchParams(window.location.search).get("stones") === "fixture" ||
  import.meta.env.VITE_STONE_FIXTURE === "1";

// Opt-in gate (feature flag, not a fallback): default = disabled everywhere, so
// no request fires per case while the real `/api/stones` endpoint doesn't exist
// yet. `?stones=fixture` enables the fixture path; set VITE_STONES_ENABLED=1
// once the endpoint is live to enable the real query.
const STONES_ENABLED = USE_FIXTURE || import.meta.env.VITE_STONES_ENABLED === "1";

export function useStoneFindingsQuery(studyUid: string): {
  stones: StoneFinding[];
  modelVersion: string | null;
  isLoading: boolean;
  error: Error | null;
} {
  const { token } = useAuth();
  const query = useQuery({
    queryKey: ["stone-findings", studyUid, USE_FIXTURE],
    enabled: STONES_ENABLED && Boolean(studyUid) && (USE_FIXTURE || Boolean(token)),
    retry: 1,
    queryFn: async () => {
      if (USE_FIXTURE) return parseStoneFindingsResponse(fixture);
      const url = `${getApiBaseUrl()}/api/stones/${encodeURIComponent(studyUid)}`;
      // Explicit header mirrors useReportQuery.ts: the AuthProvider's axios
      // request interceptor also stamps this, but the reference pattern sets
      // it explicitly per-request rather than relying on the interceptor alone.
      try {
        const res = await axios.get(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        return parseStoneFindingsResponse(res.data);
      } catch (error) {
        // The review-record endpoint is optional for cohort-backed studies:
        // model-seeded cases still have usable previews from the worklist
        // prediction, while no saved stone decisions exist yet. Treat a
        // missing row/route as an empty review rather than surfacing a red
        // request error in the right panel. Preserve auth/server failures.
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          return { stones: [], modelVersion: null };
        }
        throw error;
      }
    },
  });
  return {
    stones: query.data?.stones ?? [],
    modelVersion: query.data?.modelVersion ?? null,
    isLoading: query.isLoading,
    error: query.error as Error | null,
  };
}
